import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Skeleton } from "@mui/material";
import API from "../../API/newIndex";
import { ArrowLeft, Edit } from "feather-icons-react/build/IconComponents";
import {
  Shop,
  Telephone,
  Envelope,
  Pencil,
  StarFill,
  Globe,
} from "react-bootstrap-icons";
import Breadcrumbs from "../../controllers/breadCrumbs";
import { parentNavigate } from "../../controllers/ParentNavigate";
import Notification from "../Notification";
import { ButtonComponent } from "@shopeasy-dashboard-ui/ui-library";

const VendorDetail = () => {
  const { id } = useParams();

  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const [notification, setNotification] = useState({
    show: false,
    message: "",
    severity: "info",
  });

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const res = await API({ url: `/admin/vendor/detail/${id}`, method: "get" });

      if (res?.data?.data) setVendor(res.data.data);
    } catch (err) {
      console.error("Error fetching vendor detail:", err);
      setNotification({
        show: true,
        message: err?.response?.data?.message || err.message || "Error loading vendor detail",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const formatDateString = (dateVal) => {
    if (!dateVal) return "-";
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const formatAddress = (address) => {
    if (!address) return "";
    if (typeof address === "object") {
      const parts = [
        address.street,
        address.street2,
        address.city,
        address.state,
        address.zip,
        address.country,
      ].filter(Boolean);
      return parts.join(", ");
    }
    if (typeof address === "string") {
      try {
        const parsed = JSON.parse(address);
        if (typeof parsed === "object" && parsed !== null) {
          const parts = [
            parsed.street,
            parsed.street2,
            parsed.city,
            parsed.state,
            parsed.zip,
            parsed.country,
          ].filter(Boolean);
          return parts.length > 0 ? parts.join(", ") : address;
        }
      } catch (e) { }
    }
    return address;
  };

  const isEnabled = vendor?.vendor_selling_status === "approved" || vendor?.vendor_selling_status === "enabled";

  const tabClass = (isActive) =>
    `pb-2.5 px-3 text-xs sm:text-sm font-medium border-b-2 transition-colors duration-200 cursor-pointer ${isActive
      ? "border-[var(--btn-bg-primary)] text-[var(--primary-text-color)] font-semibold"
      : "border-transparent text-[var(--table-text-color)] hover:text-[var(--primary-text-color)]"
    }`;

  if (loading) {
    return (
      <div className="h-auto overflow-y-auto pb-[64px] p-5 w-full space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full page-header-pb gap-4">
          <div className="flex items-center space-x-2">
            <Skeleton variant="circular" width={36} height={36} />
            <div className="space-y-1">
              <Skeleton variant="text" width={140} height={16} />
              <Skeleton variant="text" width={180} height={28} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Skeleton variant="rectangular" width={100} height={32} className="rounded-md" />
            <Skeleton variant="rectangular" width={32} height={32} className="rounded-md" />
          </div>
        </div>

        <div className="secondary-bg-color rounded-[8px] border main-border-color shadow-sm p-6 space-y-4">
          <Skeleton variant="rectangular" width="100%" height={160} className="rounded-md" />
        </div>
      </div>
    );
  }

  return (
    <>
      {notification.show && (
        <Notification
          message={notification.message}
          close={() => setNotification((prev) => ({ ...prev, show: false }))}
          severity={notification.severity}
        />
      )}

      <div className="h-auto overflow-y-auto pb-[64px] p-5">
        {/* Navigation & Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full page-header-pb gap-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                parentNavigate("/vendors");
              }}
              className="back-arrow"
            >
              <ArrowLeft />
            </button>
            <div>
              <Breadcrumbs
                crumbs={[
                  { name: "Dashboard", path: "/dashboard" },
                  { name: "Vendors", path: "/vendors" },
                  { name: vendor?.shop_name || "Vendor Profile", path: `/vendors/detail/${id}` },
                ]}
              />
              <h4 className="text-xl md:text-2xl font-semibold text-primary">
                {vendor?.shop_name || "Vendor Profile"}
              </h4>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <ButtonComponent
              variant="outline"
              size="sm"
              icon={<Globe className="w-3.5 h-3.5" />}
              onClick={() => window.open(`/shop/${id}`, "_blank")}
            >
              Visit Store
            </ButtonComponent>
            <ButtonComponent
              variant="primary"
              size="sm"
              icon={<Edit className="w-3.5 h-3.5" />}
              onClick={() => parentNavigate(`/vendors/${id}`)}
            >
              Edit Vendor
            </ButtonComponent>
          </div>
        </div>

        {/* Banner & Avatar Profile Card */}
        <div className="secondary-bg-color rounded-[8px] border main-border-color shadow-sm overflow-hidden mb-5">
          <div className="h-44 bg-[var(--primary-bg-color)] relative flex items-center justify-center overflow-hidden">
            {vendor?.vendor_banner || vendor?.banner ? (
              <img
                src={vendor?.vendor_banner || vendor?.banner}
                alt="Banner"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="opacity-15 flex items-center gap-6 transform scale-125">
                <div className="w-20 h-20 rounded-xl bg-gray-300 flex items-center justify-center text-gray-500">
                  <Shop size={40} />
                </div>
                <div className="w-24 h-24 rounded-xl bg-gray-300 flex items-center justify-center text-gray-500">
                  <Shop size={48} />
                </div>
                <div className="w-20 h-20 rounded-xl bg-gray-300 flex items-center justify-center text-gray-500">
                  <Shop size={40} />
                </div>
              </div>
            )}
          </div>

          {/* Profile Header Details Bar */}
          <div className="p-6 relative pt-0 flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
            <div className="flex items-end gap-4 -mt-12">
              <div className="w-24 h-24 rounded-full secondary-bg-color p-1.5 shadow-md border main-border-color shrink-0 overflow-hidden">
                {vendor?.vendor_logo || vendor?.logo || vendor?.profile_picture || vendor?.avatar ? (
                  <img
                    src={vendor?.vendor_logo || vendor?.logo || vendor?.profile_picture || vendor?.avatar}
                    alt="Logo"
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full rounded-full primary-bg-color secondary-text-color flex items-center justify-center font-bold text-2xl border main-border-color">
                    <Shop size={36} />
                  </div>
                )}
              </div>
              <div className="pb-1">
                <h2 className="text-xl font-bold primary-text-color">
                  {vendor?.shop_name || vendor?.name || "Vendor"}
                </h2>
                <div className="flex items-center gap-1 text-xs text-amber-500 mt-1">
                  <StarFill size={12} />
                  <StarFill size={12} />
                  <StarFill size={12} />
                  <StarFill size={12} />
                  <StarFill size={12} />
                  <span className="table-text ms-1">0.0 (0)</span>
                </div>
              </div>
            </div>

            <div className="pb-1">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold ${isEnabled
                  ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 border border-emerald-200"
                  : "bg-rose-50 text-rose-600 dark:bg-rose-950/40 border border-rose-200"
                  }`}
              >
                {isEnabled ? "Enabled" : "Disabled"}
              </span>
            </div>
          </div>
        </div>

        {/* 2-Column Layout */}
        <div className="flex flex-col lg:flex-row gap-5 items-start">
          {/* Left Sidebar Info Card */}
          <div className="w-full lg:w-72 shrink-0 secondary-bg-color rounded-[8px] border main-border-color shadow-sm p-5 space-y-4 text-xs">
            <div>
              <div className="text-[11px] table-text font-medium">Registered Since:</div>
              <div className="font-semibold primary-text-color mt-0.5">
                {formatDateString(vendor?.createdAt)}
              </div>
            </div>

            {(vendor?.vendor_tagline || vendor?.tagline) && (
              <div className="pt-3 border-t main-border-color">
                <div className="text-[11px] table-text font-medium mb-1">Tagline:</div>
                <div className="primary-text-color leading-relaxed font-medium">
                  {vendor.vendor_tagline || vendor.tagline}
                </div>
              </div>
            )}

            <div className="pt-3 border-t main-border-color space-y-2.5">
              <div className="flex items-center gap-2 primary-text-color">
                <Envelope className="w-4 h-4 table-text shrink-0" />
                <span className="truncate">{vendor?.email || "—"}</span>
              </div>
              <div className="flex items-center gap-2 primary-text-color">
                <Telephone className="w-4 h-4 table-text shrink-0" />
                <span>{vendor?.phone || "—"}</span>
              </div>
            </div>

            {(vendor?.vendor_address || vendor?.business_address) && (
              <div className="pt-3 border-t main-border-color">
                <div className="text-[11px] table-text font-medium mb-1">Business Address:</div>
                <div className="primary-text-color leading-relaxed">
                  {formatAddress(vendor?.vendor_address || vendor?.business_address)}
                </div>
              </div>
            )}
          </div>

          {/* Right Main Details Card */}
          <div className="flex-1 w-full secondary-bg-color rounded-[8px] border main-border-color shadow-sm p-5 space-y-4">
            <div className="flex items-center gap-6 border-b main-border-color pb-3">
              <button
                type="button"
                onClick={() => setActiveTab("overview")}
                className={tabClass(activeTab === "overview")}
              >
                Overview
              </button>
            </div>

            {activeTab === "overview" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <div className="p-4 rounded-lg  border main-border-color space-y-1">
                  <div className="table-text font-medium">Commission Type</div>
                  <div className="font-semibold text-sm primary-text-color capitalize">
                    {vendor?.commission_type || "Percentage"}
                  </div>
                </div>
                <div className="p-4 rounded-lg  border main-border-color space-y-1">
                  <div className="table-text font-medium">Commission Rate</div>
                  <div className="font-semibold text-sm primary-text-color">
                    {vendor?.commission ?? 0}{vendor?.commission_type === "fixed" ? " $" : " %"}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default VendorDetail;
